import axios from "axios";
import { message } from "antd";

// TSK-5056: a print whose till template isn't configured (or can't be read) used to skip silently,
// print a blank slip, or throw inside the printer. Every such case now tells the cashier which print
// was skipped and which template on the till needs to be configured.
export const PRINT_TEMPLATES = {
  sales: "Sales Print Template",
  kot: "KOT Print Template",
  estimate: "Estimate Print Template",
  cancelKot: "Cancel KOT Print Template",
  tillClose: "Till Close Template",
  salesReport: "Sales Report Template",
  giftCard: "Gift Card / Credit Note / Voucher Print Template",
};

// Keyed per template+print so a per-vendor/per-copy loop shows one toast, not one per iteration.
const showError = (key, content) => {
  console.error(`[PrintTemplate] ${content}`);
  try {
    message.error({ content, key: `print-template-${key}`, duration: 6 });
  } catch {
    /* message unavailable (e.g. outside the React tree) -- console error above still records it */
  }
};

export const notifyTemplateNotConfigured = (templateName, printName) =>
  showError(`${templateName}-${printName}-missing`, `${printName} not printed: ${templateName} is not configured for this till. Configure it in the Till window and log in again.`);

export const notifyTemplateInvalid = (templateName, printName) =>
  showError(`${templateName}-${printName}-invalid`, `${printName} not printed: ${templateName} configured for this till is not in a valid format. Please correct the template.`);

export const notifyNoPrinterSelected = () =>
  showError("no-default-printer", "Not printed: no printer is selected in CW Browser printer settings. Select a default printer and print again.");

// Every printer file POSTs to `${hardwareController.imageUrl}printer` and most only console.error a
// failure. One response interceptor turns any failed call to the print service into a visible error.
let printServiceNotifierInstalled = false;
export const installPrintServiceErrorNotifier = () => {
  if (printServiceNotifierInstalled) return;
  printServiceNotifierInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const url = error?.config?.url || "";
      // Pole display (fires on every cart add) and cash drawer share this endpoint but aren't prints.
      const body = typeof error?.config?.data === "string" ? error.config.data : "";
      const isNonPrintCommand = body.includes("pole_display") || body.includes("cash_drawer");
      const isPrintServiceCall = error?.config?.method?.toLowerCase() === "post" && /printer\/?$/.test(url) && !isNonPrintCommand;
      if (isPrintServiceCall && !axios.isCancel(error)) {
        if (/^(undefined|null)printer/.test(url)) {
          showError("print-service-url-missing", "Not printed: Hardware Controller (print service URL) is not configured for this till.");
        } else {
          showError("print-service-failed", `Not printed: print service is not reachable (${url.replace(/printer\/?$/, "")}). Check that the print service is running and the printer is connected.`);
        }
      }
      return Promise.reject(error);
    }
  );
};

// Parses a JSON (CW hardware controller) template; returns null and shows the matching error when
// the template is missing or unparseable, so callers can simply bail out on null.
export const parseTemplateOrNotify = (templateCode, templateName, printName) => {
  if (!templateCode) {
    notifyTemplateNotConfigured(templateName, printName);
    return null;
  }
  try {
    const parsed = JSON.parse(templateCode);
    if (!parsed || !Array.isArray(parsed.elements)) throw new Error("template has no elements array");
    return parsed;
  } catch (error) {
    console.error(`[PrintTemplate] ${templateName} parse failed for ${printName}:`, error?.message);
    notifyTemplateInvalid(templateName, printName);
    return null;
  }
};
